import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAdoptedTools, groupToolsForDock, iconFor, toolName } from "../../../marketplace";
import type { TherapyTool } from "../../../marketplace";
import { launchConstellationFor } from "../../application/constellation";
import { SchedulerDockActions } from "../../../scheduler";
import { PaymentsDockActions } from "../../../payments";
import { SystemicMapDockActions } from "../../../systemic_map";
import { useStartEmdrSession } from "../../../emdr";
import ToolDock, { type ToolDockGroup } from "../../../../shared/ui/ToolDock";

/**
 * How an adopted tool behaves when its dock entry is activated.
 *
 * A map rather than a slug comparison inside the click handler: adding another
 * tool is an entry here, not a new branch in the middle of the JSX. Only tools
 * listed here render in the dock.
 */
const ACTIVATION: Record<string, "navigate" | "sheet" | "session"> = {
    constellation: "navigate",
    scheduler: "sheet",
    payments: "sheet",
    "genogram-ecomap": "sheet",
    // A new session with this patient, and its board in a new tab.
    emdr: "session",
};

// Individual patient board vs. couple board — the dock launches the matching
// constellation session, or schedules against, whichever subject the page
// passes in.
type Props =
    | { patientId: string; couplePatientId?: never; groupPatientId?: never; subjectLabel?: string }
    | { couplePatientId: string; patientId?: never; groupPatientId?: never; subjectLabel?: string }
    | { groupPatientId: string; patientId?: never; couplePatientId?: never; subjectLabel?: string };

/** What a group's dock offers: payments are owed by one person, not a group. */
const GROUP_TOOLS = new Set(["scheduler", "constellation", "genogram-ecomap"]);

/**
 * Tools that only make sense for one person. EMDR is worked one patient at a
 * time, and its ratings are kept on individual sessions only.
 */
const INDIVIDUAL_ONLY_TOOLS = new Set(["emdr"]);

export default function PatientToolDock({
    patientId,
    couplePatientId,
    groupPatientId,
    subjectLabel,
}: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: tools, isLoading } = useAdoptedTools();
    const [launchingSlug, setLaunchingSlug] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // Which tool's sheet is open, not merely whether one is: more than one
    // tool activates by sheet now.
    const [openSheetSlug, setOpenSheetSlug] = useState<string | null>(null);
    const emdr = useStartEmdrSession();

    /**
     * Grouped the way the home dock groups them: administration first, then
     * the clinical instruments by the way of working they belong to. Two docks
     * that order the same tools differently make a therapist look twice.
     */
    const grouped = useMemo(
        () =>
            tools
                ? groupToolsForDock(
                      tools.filter(
                          (tool) =>
                              ACTIVATION[tool.slug] &&
                              (!groupPatientId || GROUP_TOOLS.has(tool.slug)) &&
                              (Boolean(patientId) || !INDIVIDUAL_ONLY_TOOLS.has(tool.slug))
                      )
                  )
                : [],
        [tools, groupPatientId, patientId],
    );

    const launchPath = async (): Promise<string> => {
        // Only constellation navigates. Couple pages launch a couple board;
        // individual pages an individual board.
        const data = await launchConstellationFor({ patientId, couplePatientId, groupPatientId });
        return `/constellation/${data.board.id}`;
    };

    const handleNavigate = async (tool: TherapyTool) => {
        if (launchingSlug) return;
        setError(null);
        setLaunchingSlug(tool.slug);
        try {
            const path = await launchPath();
            navigate(path);
        } catch {
            setError(t("constellation.dock.launchError"));
            setLaunchingSlug(null);
        }
    };

    const handleActivate = (tool: TherapyTool) => {
        if (ACTIVATION[tool.slug] === "session") {
            setError(null);
            void emdr
                .start({ id: patientId as string, name: subjectLabel ?? "" })
                .then((started) => {
                    if (!started) setError(t("emdr.dock.startError"));
                });
            return;
        }
        if (ACTIVATION[tool.slug] === "sheet") {
            setOpenSheetSlug(tool.slug);
            return;
        }
        void handleNavigate(tool);
    };

    const groups: ToolDockGroup[] = grouped.map((group) => ({
        key: group.key,
        label: t(group.labelKey),
        shortLabel: group.shortLabelKey ? t(group.shortLabelKey) : undefined,
        items: group.tools.map((tool) => {
            const Icon = iconFor(tool.icon);
            return {
                id: tool.id,
                label: toolName(t, tool),
                icon: <Icon className="h-5 w-5" />,
                busy: launchingSlug === tool.slug || (tool.slug === "emdr" && emdr.starting),
                disabled: Boolean(launchingSlug) || emdr.starting,
                onSelect: () => handleActivate(tool),
            };
        }),
    }));

    if (isLoading || !tools) return null;

    const launchable: TherapyTool[] = grouped.flatMap((group) => group.tools);
    if (launchable.length === 0) return null;

    const schedulerAdopted = launchable.some((tool) => tool.slug === "scheduler");
    // Payments are owed by an individual, and the API takes a patient id. A
    // couple page has no such id, so the tool stays out of that dock rather
    // than opening a form that cannot be submitted.
    const paymentsAdopted =
        launchable.some((tool) => tool.slug === "payments") && Boolean(patientId);
    const systemicMapAdopted = launchable.some(
        (tool) => tool.slug === "genogram-ecomap"
    );

    const subject = {
        kind: (groupPatientId ? "group" : couplePatientId ? "couple" : "patient") as
            | "group"
            | "couple"
            | "patient",
        id: (groupPatientId ?? couplePatientId ?? patientId) as string,
    };

    return (
        <>
            <ToolDock
                groups={groups}
                ariaLabel={t("constellation.dock.title")}
                error={error}
            />

            {/* Mounted only once the tool is adopted, so its queries never run
                for a therapist who does not have it. */}
            {schedulerAdopted && (
                <SchedulerDockActions
                    isOpen={openSheetSlug === "scheduler"}
                    onClose={() => setOpenSheetSlug(null)}
                    subject={{
                        ...subject,
                        label: subjectLabel ?? t("scheduler.schedule.thisPatient"),
                    }}
                />
            )}
            {paymentsAdopted && (
                <PaymentsDockActions
                    isOpen={openSheetSlug === "payments"}
                    onClose={() => setOpenSheetSlug(null)}
                    subject={{
                        id: patientId as string,
                        label: subjectLabel ?? t("payments.launchSheet.thisPatient"),
                    }}
                />
            )}
            {systemicMapAdopted && (
                <SystemicMapDockActions
                    isOpen={openSheetSlug === "genogram-ecomap"}
                    onClose={() => setOpenSheetSlug(null)}
                    subject={{
                        ...subject,
                        label: subjectLabel ?? t("systemicMap.toolName"),
                    }}
                />
            )}
        </>
    );
}
